import { Link, useLocation, useNavigate } from 'react-router';
import { navigationStateFrom } from '@/types/navigation';
import { Eye } from '@untitledui/icons';
import { useTranslation } from 'react-i18next';
import type { SortDescriptor } from 'react-aria-components';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { useI18n } from '@/hooks/use-i18n';
import type { Container } from '@/types/container';
import { ContainerRouteProgress } from './container-route-progress';

interface RecentContainersTableProps {
    /** Array of containers to display */
    containers: Container[];
    /** Maximum number of containers to show (default: 10) */
    maxCount?: number;
    /** Optional callback when clicking on a container */
    onContainerClick?: (id: string) => void;
    /** Optional search params to preserve view mode in links */
    searchParams?: URLSearchParams;
    /** Optional title override */
    title?: string;
    /** Optional link text override */
    viewAllLinkText?: string;
    /** Optional callback for view all link click */
    onViewAllClick?: () => void;
    /** Whether to show simplified columns (for dashboard) or full columns (for containers page) */
    simplified?: boolean;
    /** Current sort field */
    sortBy?: string;
    /** Current sort order */
    sortOrder?: 'asc' | 'desc';
    /** Callback when sort changes */
    onSortChange?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
}

// Helper to format last update timestamp (returns date string)
const formatLastUpdateDate = (date: Date | undefined): string => {
    if (!date) return '-';
    return date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

// Helper to get last update type label
const getLastUpdateTypeLabel = (type: string | undefined): string => {
    return type || 'Update';
};

// Helper to format relative time ("2h ago", "1d ago", etc.)
const formatRelativeTime = (date: Date | undefined): string => {
    if (!date) return '-';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60_000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `${diffDays}d ago`;
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
};

// Helper functions for color mapping
const getRiskLevelColor = (riskLevel: string) => {
    switch (riskLevel) {
        case 'Low': return 'success';
        case 'Medium': return 'warning';
        case 'High': return 'error';
        case 'Critical': return 'error';
        default: return 'gray';
    }
};

const getDocumentStatusColor = (status: string) => {
    switch (status) {
        case 'Completo': return 'success';
        case 'Pending': return 'warning';
        case 'Error': return 'error';
        default: return 'gray';
    }
};

const getStatusColor = (status: string) => {
    switch (status) {
        case 'Draft': return 'gray';
        case 'Booked': return 'brand';
        case 'In Transit': return 'brand';
        case 'Arrived': return 'warning';
        case 'Delivered': return 'success';
        case 'On Hold': return 'warning';
        case 'Cancelled': return 'error';
        default: return 'gray';
    }
};

export function RecentContainersTable({
    containers,
    maxCount = 10,
    onContainerClick,
    searchParams,
    title,
    viewAllLinkText,
    onViewAllClick,
    simplified = false,
    sortBy,
    sortOrder = 'asc',
    onSortChange,
}: RecentContainersTableProps) {
    const { t } = useTranslation(['pages:containers', 'pages:dashboard']);
    const { translateContainerStatus, translateDocumentStatus, translateRiskLevel } = useI18n();
    const location = useLocation();
    const navigate = useNavigate();

    const displayedContainers = containers.slice(0, maxCount);
    const displayTitle = title || t('shipper.recentContainers.title', { ns: 'pages:dashboard' });
    const linkText = viewAllLinkText || t('shipper.recentContainers.viewAll', { ns: 'pages:dashboard' });

    // Handle sort change from table
    const handleSortChange = (descriptor: SortDescriptor) => {
        if (onSortChange && descriptor.column) {
            const newOrder = descriptor.direction === 'ascending' ? 'asc' : 'desc';
            onSortChange(String(descriptor.column), newOrder);
        }
    };

    // Build sort descriptor for the table
    const sortDescriptor: SortDescriptor | undefined = sortBy
        ? { column: sortBy, direction: sortOrder === 'asc' ? 'ascending' : 'descending' }
        : undefined;

    const handleContainerClick = (containerId: string) => {
        if (onContainerClick) {
            onContainerClick(containerId);
        }
    };

    const handleRowClick = (containerId: string) => {
        const currentView = searchParams?.get('view');
        const url = currentView
            ? `/shipper/containers/${containerId}?view=${currentView}`
            : `/shipper/containers/${containerId}`;
        navigate(url, { state: navigationStateFrom(location) });
        handleContainerClick(containerId);
    };

    const handleViewAllClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (onViewAllClick) {
            e.preventDefault();
            onViewAllClick();
        }
    };

    if (simplified) {
        // Simplified version for dashboard
        return (
            <TableCard.Root>
                <TableCard.Header
                    title={displayTitle}
                    description={t('shipper.recentContainers.description', { ns: 'pages:dashboard' })}
                    contentTrailing={
                        <div className="flex items-center gap-2">
                            <Link
                                to="/shipper/containers"
                                className="inline-flex items-center justify-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                                onClick={handleViewAllClick}
                            >
                                <Eye className="size-4" />
                                {linkText}
                            </Link>
                        </div>
                    }
                />
                <Table>
                    <Table.Header>
                        <Table.Head label={t('shipper.recentContainers.headers.containerId', { ns: 'pages:dashboard' })} isRowHeader />
                        <Table.Head label={t('shipper.recentContainers.headers.route', { ns: 'pages:dashboard' })} className="min-w-[280px]" />
                        <Table.Head label={t('shipper.recentContainers.headers.eta', { ns: 'pages:dashboard' })} />
                        <Table.Head label={t('shipper.recentContainers.headers.docStatus', { ns: 'pages:dashboard' })} />
                        <Table.Head label={t('shipper.recentContainers.headers.riskLevel', { ns: 'pages:dashboard' })} />
                        <Table.Head label={t('shipper.recentContainers.headers.value', { ns: 'pages:dashboard' })} />
                    </Table.Header>
                    <Table.Body>
                        {displayedContainers.map((container) => (
                            <Table.Row
                                key={container.id}
                                className="cursor-pointer hover:bg-secondary"
                                onClick={() => handleRowClick(container.id)}
                            >
                                <Table.Cell>
                                    <div className="font-medium text-primary">{container.containerNumber}</div>
                                </Table.Cell>
                                <Table.Cell>
                                    <ContainerRouteProgress
                                    container={container}
                                    showPortCodeBadges={false}
                                    showCenterProgressBetweenPorts
                                />
                                </Table.Cell>
                                <Table.Cell>
                                    {container.eta ? (
                                        <span className="text-sm text-tertiary">
                                            {container.eta.toLocaleDateString('it-IT')}
                                        </span>
                                    ) : (container.dateReferenceSource === 'booking_date' || container.dateReferenceSource === 'created_at') ? (
                                        <span className="text-[10px] text-warning-500">No ETA</span>
                                    ) : (
                                        <span className="text-sm text-tertiary">-</span>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    <Badge color={getDocumentStatusColor(container.documentStatus)} size="sm">
                                        {translateDocumentStatus(container.documentStatus)}
                                    </Badge>
                                </Table.Cell>
                                <Table.Cell>
                                    <Badge color={getRiskLevelColor(container.riskLevel)} size="sm">
                                        {translateRiskLevel(container.riskLevel)}
                                    </Badge>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">
                                        {container.value.currency} {container.value.amount.toLocaleString()}
                                    </span>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>
        );
    }

    // Full version for containers page
    return (
        <TableCard.Root className="relative">
            <Table sortDescriptor={sortDescriptor} onSortChange={handleSortChange}>
                <Table.Header>
                    <Table.Head label={t('list.table.headers.containerNumber')} isRowHeader />
                    <Table.Head id="status" label={t('list.table.headers.status')} allowsSorting />
                    <Table.Head id="risk" label={t('list.table.headers.riskLevel')} allowsSorting />
                    <Table.Head id="progress" label={t('list.table.headers.route')} className="min-w-[340px]" allowsSorting />
                    <Table.Head label={t('list.table.headers.carrier')} />
                    <Table.Head label={t('list.table.headers.receiver')} />
                    <Table.Head id="etd" label="ETD" allowsSorting />
                    <Table.Head id="eta" label="ETA" allowsSorting />
                    <Table.Head id="delay" label="Delay" allowsSorting />
                    <Table.Head id="documents" label="Documents" allowsSorting />
                    <Table.Head id="doc_issues" label="Doc Status" allowsSorting />
                    <Table.Head id="issues" label="Extra-Doc Issues" allowsSorting />
                    <Table.Head id="last_update" label="Last Update" allowsSorting />
                    <Table.Head id="last_tracked" label="Last Tracked" allowsSorting />
                </Table.Header>
                <Table.Body>
                    {displayedContainers.map((container) => (
                        <Table.Row
                            key={container.id}
                            className="cursor-pointer hover:bg-secondary"
                            onClick={() => handleRowClick(container.id)}
                        >
                            <Table.Cell>
                                <div className="font-medium text-primary">{container.containerNumber}</div>
                            </Table.Cell>
                            <Table.Cell>
                                <Badge color={getStatusColor(container.status)} size="sm">
                                    {translateContainerStatus(container.status)}
                                </Badge>
                            </Table.Cell>
                            <Table.Cell>
                                <Badge color={getRiskLevelColor(container.riskLevel)} size="sm">
                                    {translateRiskLevel(container.riskLevel)}
                                </Badge>
                            </Table.Cell>
                            <Table.Cell>
                                <ContainerRouteProgress
                                    container={container}
                                    showPortCodeBadges={false}
                                    showCenterProgressBetweenPorts
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <div className="text-sm text-tertiary">{container.shippingCompany ?? '-'}</div>
                            </Table.Cell>
                            <Table.Cell>
                                <div className="text-sm text-tertiary truncate max-w-[140px]" title={container.consigneeName}>{container.consigneeName ?? '-'}</div>
                            </Table.Cell>
                            <Table.Cell>
                                {container.estimatedDepartureDate ? (
                                    <span className="text-sm text-tertiary">
                                        {container.estimatedDepartureDate.toLocaleDateString('it-IT')}
                                    </span>
                                ) : container.dateReferenceSource === 'booking_date' && container.bookingDate ? (
                                    <span className="text-sm text-quaternary" title="No ETD — showing booking date">
                                        {container.bookingDate.toLocaleDateString('it-IT')} <span className="text-[10px] text-warning-500">BKG</span>
                                    </span>
                                ) : container.dateReferenceSource === 'created_at' ? (
                                    <span className="text-sm text-quaternary" title="No timeline dates — showing creation date">
                                        {container.createdAt?.toLocaleDateString('it-IT')} <span className="text-[10px] text-warning-500">NEW</span>
                                    </span>
                                ) : (
                                    <span className="text-sm text-tertiary">-</span>
                                )}
                            </Table.Cell>
                            <Table.Cell>
                                {container.eta ? (
                                    <span className="text-sm text-tertiary">
                                        {container.eta.toLocaleDateString('it-IT')}
                                    </span>
                                ) : (container.dateReferenceSource === 'booking_date' || container.dateReferenceSource === 'created_at') ? (
                                    <span className="text-[10px] text-warning-500">No ETA</span>
                                ) : (
                                    <span className="text-sm text-tertiary">-</span>
                                )}
                            </Table.Cell>
                            <Table.Cell>
                                {(() => {
                                    const delay = container.delayDays;
                                    if (delay == null || delay === 0) return <span className="text-sm text-tertiary">—</span>;
                                    const badgeColor = delay >= 7 ? 'error' : delay >= 3 ? 'warning' : 'gray';
                                    return (
                                        <Badge color={badgeColor} size="sm">
                                            {delay > 0 ? `+${delay}d` : `${delay}d`}
                                        </Badge>
                                    );
                                })()}
                            </Table.Cell>
                            <Table.Cell>
                                <span className="text-sm text-tertiary">{container.documentCount ?? 0}</span>
                            </Table.Cell>
                            <Table.Cell>
                                {(() => {
                                    const docIssueCount = container.docsWithIssues ?? 0;
                                    const noDocUploaded = container.documentStatusOverall === 'No documents uploaded';
                                    const allOk = docIssueCount === 0 && !noDocUploaded;
                                    return (
                                        <span className={`text-sm ${allOk ? 'text-success-600' : noDocUploaded ? 'text-tertiary' : 'text-warning-600'}`}>
                                            {noDocUploaded
                                                ? 'No documents'
                                                : allOk
                                                    ? 'All docs OK'
                                                    : `${docIssueCount} issue${docIssueCount !== 1 ? 's' : ''} in docs`}
                                        </span>
                                    );
                                })()}
                            </Table.Cell>
                            <Table.Cell>
                                {(() => {
                                    const issueMatch = container.exogenStatusOverall?.match(/^(\d+)/);
                                    const issueCount = issueMatch ? parseInt(issueMatch[1], 10) : 0;
                                    const allSystemsOk = container.exogenStatusOverall === 'All systems OK' || issueCount === 0;
                                    return (
                                        <span className={`text-sm ${allSystemsOk ? 'text-success-600' : 'text-warning-600'}`}>
                                            {allSystemsOk
                                                ? 'No issues'
                                                : `${issueCount} issue${issueCount !== 1 ? 's' : ''}`}
                                        </span>
                                    );
                                })()}
                            </Table.Cell>
                            <Table.Cell>
                                <div className="flex flex-col">
                                    <span className="text-sm text-tertiary whitespace-nowrap">
                                        {formatLastUpdateDate(container.lastUpdateAt)}
                                    </span>
                                    <span className="text-xs text-quaternary">
                                        {getLastUpdateTypeLabel(container.lastUpdateType)}
                                    </span>
                                </div>
                            </Table.Cell>
                            <Table.Cell>
                                <div className="flex flex-col">
                                    <span className="text-sm text-tertiary whitespace-nowrap">
                                        {formatRelativeTime(container.lastTrackedAt)}
                                    </span>
                                    {container.trackingProvider && (
                                        <span className="text-xs text-quaternary">
                                            {container.trackingProvider}
                                        </span>
                                    )}
                                </div>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
            {/* Edge gradients: fade from edge inward (shadow into content) */}
            <div
                aria-hidden
                className="pointer-events-none absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-primary to-transparent dark:from-gray-800"
            />
            {/* No vertical clamp: table grows to show all current page rows */}
        </TableCard.Root>
    );
}
